import { CachedDataCollector } from "src/core/base/CachedDataCollector";
import { TrackerVariableType } from "src/core/model/TrackerVariableType";
import { CacheStrategy } from "src/core/model/VariableCollectionMetadata";
import { RunningStats } from "src/util/RunningStats";
import { throttle } from "src/util/throttle";

const MAX_PATH_SEGMENTS = 20;
const PAUSE_THRESHOLD_MS = 200;
const IDLE_THRESHOLD_MS = 5000;
const IDLE_CHECK_INTERVAL_MS = 5000;
const INTERACTION_WINDOW_MS = 60_000;
const BURST_WINDOW_MS = 3000;
const BURST_MIN_EVENTS = 5;
const TAP_MAX_DURATION_MS = 300;
const SWIPE_MIN_DISPLACEMENT_PX = 50;
const INTER_KEY_MAX_GAP_MS = 2000;

interface PathSegment {
  displacement: number;
  pathLength: number;
}

/**
 * Collects behavioral biometric signals from user interactions.
 * Computes aggregate statistics from mouse, keyboard, scroll, session, and touch data.
 *
 * Design principles:
 * - All DOM listeners use `{ passive: true }` to avoid blocking the main thread
 * - High-frequency events (mousemove, scroll, touchmove) are throttled
 * - All statistics use Welford's online algorithm (O(1) memory)
 * - No raw event streams stored — only summary aggregates
 * - `collect()` returns pre-computed summaries; called by EventCoordinator on existing events
 */
export class BehavioralCollector extends CachedDataCollector {
  // === Mouse tracking state ===
  private mouseVelocityStats = new RunningStats();
  private mouseAccelStats = new RunningStats();
  private lastMouseSample: { x: number; y: number; t: number; velocity: number } | null = null;
  private pathSegments: PathSegment[] = [];
  private currentPathLength = 0;
  private currentPathStartX = 0;
  private currentPathStartY = 0;
  private angularVarianceStats = new RunningStats();
  private lastAngle: number | null = null;
  private lastMouseSampleTs = 0;

  // === Keystroke tracking state ===
  private lastKeydownTs = 0;
  private lastKeyupTs = 0;
  private interKeyStats = new RunningStats();
  private dwellTimeStats = new RunningStats();
  private flightTimeStats = new RunningStats();
  private pendingKeydowns = new Map<string, number>();

  // === Scroll tracking state ===
  private lastScrollY = 0;
  private lastScrollTs = 0;
  private maxScrollDepthPx = 0;
  private scrollSpeedStats = new RunningStats();
  private scrollDirectionChanges = 0;
  private lastScrollDirection = 0;
  private scrollTotalDistance = 0;
  private smoothScrollCount = 0;
  private totalScrollSamples = 0;

  // === Session tracking state ===
  private lastAnyInteractionTs = Date.now();
  private idleCount = 0;
  private totalIdleMs = 0;
  private maxIdleMs = 0;
  private currentIdleStart: number | null = null;
  private tabSwitchCount = 0;
  private totalHiddenMs = 0;
  private lastHiddenTs: number | null = null;
  private interactionTimestamps: number[] = [];
  private burstCount = 0;

  // === Touch tracking state ===
  private touchSupported = false;
  private touchRadiusStats = new RunningStats();
  private touchPressureStats = new RunningStats();
  private swipeVelocityStats = new RunningStats();
  private tapCount = 0;
  private swipeCount = 0;
  private activeTouchStart: { x: number; y: number; t: number } | null = null;

  // === Lifecycle ===
  private listenersAttached = false;
  private idleCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private initTs = 0;

  // === Bound handlers (for cleanup) ===
  private boundMouseHandler: ((e: MouseEvent) => void) | null = null;
  private boundKeydownHandler: ((e: KeyboardEvent) => void) | null = null;
  private boundKeyupHandler: ((e: KeyboardEvent) => void) | null = null;
  private boundScrollHandler: (() => void) | null = null;
  private boundVisibilityHandler: (() => void) | null = null;
  private boundTouchStartHandler: ((e: TouchEvent) => void) | null = null;
  private boundTouchMoveHandler: ((e: TouchEvent) => void) | null = null;
  private boundTouchEndHandler: ((e: TouchEvent) => void) | null = null;

  collect(): Map<TrackerVariableType, unknown> {
    const data = new Map<TrackerVariableType, unknown>();
    const sessionAge = Date.now() - this.initTs;

    this.collectWithCache(data, TrackerVariableType.MOUSE_DYNAMICS, CacheStrategy.FAST, () =>
      this.computeMouseDynamics(),
    );

    this.collectWithCache(data, TrackerVariableType.KEYSTROKE_DYNAMICS, CacheStrategy.FAST, () =>
      this.computeKeystrokeDynamics(),
    );

    this.collectWithCache(data, TrackerVariableType.SCROLL_BEHAVIOR, CacheStrategy.FAST, () =>
      this.computeScrollBehavior(),
    );

    this.collectWithCache(data, TrackerVariableType.SESSION_BEHAVIOR, CacheStrategy.FAST, () =>
      this.computeSessionBehavior(),
    );

    if (this.touchSupported) {
      this.collectWithCache(data, TrackerVariableType.TOUCH_DYNAMICS, CacheStrategy.FAST, () =>
        this.computeTouchDynamics(),
      );
    } else {
      this.collectIfNeeded(data, TrackerVariableType.TOUCH_DYNAMICS, () => null);
    }

    this.collectIfNeeded(data, TrackerVariableType.BEHAVIORAL_BIOMETRIC_SCORE, () =>
      this.computeBiometricScore(sessionAge),
    );

    this.collectIfNeeded(data, TrackerVariableType.BEHAVIORAL_BIOMETRIC_FLAGS, () =>
      this.computeBiometricFlags(sessionAge),
    );

    return data;
  }

  // === Lifecycle ===

  init(): void {
    this.initTs = Date.now();
    this.lastAnyInteractionTs = Date.now();
    this.touchSupported = "ontouchstart" in window;
    this.attachListeners();
    this.startIdleCheck();
  }

  destroy(): void {
    this.detachListeners();
    this.stopIdleCheck();
  }

  resetRollingWindow(): void {
    // Prune interaction timestamps to last 60s
    const cutoff = Date.now() - INTERACTION_WINDOW_MS;
    this.interactionTimestamps = this.interactionTimestamps.filter((t) => t >= cutoff);
  }

  // === Mouse dynamics computation ===

  private computeMouseDynamics(): {
    velocity: { mean: number; max: number; stddev: number };
    acceleration: { mean: number; max: number };
    straightness: number;
    jitter: number;
    sampleCount: number;
  } {
    const vStats = this.mouseVelocityStats;
    const aStats = this.mouseAccelStats;

    let straightness = 0;
    if (this.pathSegments.length > 0) {
      let totalRatio = 0;
      for (const seg of this.pathSegments) {
        totalRatio += seg.pathLength > 0 ? seg.displacement / seg.pathLength : 0;
      }
      straightness = totalRatio / this.pathSegments.length;
    }

    const angStats = this.angularVarianceStats;
    const jitter = angStats.getCount() > 1 ? Math.min(1, angStats.getStddev() / Math.PI) : 0;

    return {
      velocity: { mean: vStats.getMean(), max: vStats.getMax(), stddev: vStats.getStddev() },
      acceleration: { mean: aStats.getMean(), max: aStats.getMax() },
      straightness,
      jitter,
      sampleCount: vStats.getCount(),
    };
  }

  // === Keystroke dynamics computation ===

  private computeKeystrokeDynamics(): {
    interKeyDelay: { mean: number; stddev: number; min: number; max: number };
    dwellTime: { mean: number; stddev: number };
    flightTime: { mean: number; stddev: number };
    rhythmConsistency: number;
    sampleCount: number;
  } {
    const ikStats = this.interKeyStats;
    const mean = ikStats.getMean();
    const stddev = ikStats.getStddev();
    const rhythmConsistency = mean > 0 && ikStats.getCount() > 2 ? Math.max(0, Math.min(1, 1 - stddev / mean)) : 0;

    return {
      interKeyDelay: { mean, stddev, min: ikStats.getMin(), max: ikStats.getMax() },
      dwellTime: { mean: this.dwellTimeStats.getMean(), stddev: this.dwellTimeStats.getStddev() },
      flightTime: { mean: this.flightTimeStats.getMean(), stddev: this.flightTimeStats.getStddev() },
      rhythmConsistency,
      sampleCount: ikStats.getCount(),
    };
  }

  // === Scroll behavior computation ===

  private computeScrollBehavior(): {
    maxDepthPercent: number;
    avgSpeed: number;
    directionChanges: number;
    totalDistance: number;
    smoothness: number;
    sampleCount: number;
  } {
    const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
    const maxDepthPercent = pageHeight > 0 ? Math.min(100, (this.maxScrollDepthPx / pageHeight) * 100) : 0;

    return {
      maxDepthPercent,
      avgSpeed: this.scrollSpeedStats.getMean(),
      directionChanges: this.scrollDirectionChanges,
      totalDistance: this.scrollTotalDistance,
      smoothness: this.totalScrollSamples > 0 ? this.smoothScrollCount / this.totalScrollSamples : 0,
      sampleCount: this.totalScrollSamples,
    };
  }

  // === Session behavior computation ===

  private computeSessionBehavior(): {
    idle: { count: number; totalMs: number; maxMs: number };
    focus: { tabSwitchCount: number; totalHiddenMs: number };
    tempo: { interactionsPerMinute: number; burstCount: number };
  } {
    const now = Date.now();
    const cutoff = now - INTERACTION_WINDOW_MS;
    const recentInteractions = this.interactionTimestamps.filter((t) => t >= cutoff).length;

    return {
      idle: { count: this.idleCount, totalMs: this.totalIdleMs, maxMs: this.maxIdleMs },
      focus: { tabSwitchCount: this.tabSwitchCount, totalHiddenMs: this.totalHiddenMs },
      tempo: { interactionsPerMinute: recentInteractions, burstCount: this.burstCount },
    };
  }

  // === Touch dynamics computation ===

  private computeTouchDynamics(): {
    avgTouchRadius: number;
    avgPressure: number;
    swipeVelocity: { mean: number; max: number };
    tapCount: number;
    swipeCount: number;
    sampleCount: number;
  } {
    return {
      avgTouchRadius: this.touchRadiusStats.getMean(),
      avgPressure: this.touchPressureStats.getMean(),
      swipeVelocity: { mean: this.swipeVelocityStats.getMean(), max: this.swipeVelocityStats.getMax() },
      tapCount: this.tapCount,
      swipeCount: this.swipeCount,
      sampleCount: this.touchRadiusStats.getCount(),
    };
  }

  // === Composite biometric score ===

  private computeBiometricScore(sessionAgeMs: number): number {
    let score = 0;
    const minAge = 5000; // Need at least 5s of data

    if (sessionAgeMs < minAge) return 0;

    // Mouse anomaly (weight: 0.25)
    const mouseDyn = this.computeMouseDynamics();
    let mouseAnomaly = 0;
    if (mouseDyn.sampleCount > 10) {
      if (mouseDyn.straightness > 0.95) mouseAnomaly += 0.4;
      if (mouseDyn.jitter < 0.05) mouseAnomaly += 0.3;
      if (mouseDyn.velocity.stddev < 0.01 && mouseDyn.velocity.mean > 0) mouseAnomaly += 0.3;
    }
    score += Math.min(1, mouseAnomaly) * 0.25;

    // Keystroke anomaly (weight: 0.20)
    const keyDyn = this.computeKeystrokeDynamics();
    let keystrokeAnomaly = 0;
    if (keyDyn.sampleCount > 10) {
      if (keyDyn.rhythmConsistency > 0.9) keystrokeAnomaly += 0.5;
      if (keyDyn.dwellTime.mean < 10 && keyDyn.dwellTime.mean > 0) keystrokeAnomaly += 0.5;
    }
    score += Math.min(1, keystrokeAnomaly) * 0.2;

    // Scroll anomaly (weight: 0.15)
    const scrollBeh = this.computeScrollBehavior();
    let scrollAnomaly = 0;
    if (scrollBeh.sampleCount > 3) {
      if (scrollBeh.smoothness < 0.2) scrollAnomaly += 0.5;
      if (scrollBeh.directionChanges === 0 && scrollBeh.totalDistance > 500) scrollAnomaly += 0.5;
    }
    score += Math.min(1, scrollAnomaly) * 0.15;

    // Session anomaly (weight: 0.15)
    const sessBeh = this.computeSessionBehavior();
    let sessionAnomaly = 0;
    if (sessionAgeMs > 60_000) {
      if (sessBeh.idle.count === 0) sessionAnomaly += 0.3;
      if (sessBeh.focus.tabSwitchCount === 0) sessionAnomaly += 0.2;
    }
    if (sessBeh.tempo.interactionsPerMinute > 200) sessionAnomaly += 0.5;
    else if (sessBeh.tempo.interactionsPerMinute < 1 && sessionAgeMs > 30_000) sessionAnomaly += 0.3;
    score += Math.min(1, sessionAnomaly) * 0.15;

    // Touch anomaly (weight: 0.10)
    if (this.touchSupported) {
      const touchDyn = this.computeTouchDynamics();
      let touchAnomaly = 0;
      if (touchDyn.sampleCount > 5) {
        if (touchDyn.avgPressure === 0) touchAnomaly += 0.5;
        if (touchDyn.avgTouchRadius === 0) touchAnomaly += 0.5;
      }
      score += Math.min(1, touchAnomaly) * 0.1;
    }

    // Data absence (weight: 0.15)
    let absenceAnomaly = 0;
    if (sessionAgeMs > 30_000) {
      if (mouseDyn.sampleCount === 0 && !this.touchSupported) absenceAnomaly += 0.33;
      if (keyDyn.sampleCount === 0) absenceAnomaly += 0.33;
      if (scrollBeh.sampleCount === 0) absenceAnomaly += 0.34;
    }
    score += Math.min(1, absenceAnomaly) * 0.15;

    return Math.min(1, score);
  }

  // === Biometric flags ===

  private computeBiometricFlags(sessionAgeMs: number): {
    perfectMouseLinearity: boolean;
    zeroMouseJitter: boolean;
    uniformKeystrokeTiming: boolean;
    noKeystrokeData: boolean;
    programmaticScrolling: boolean;
    noIdlePeriods: boolean;
    noTouchPressure: boolean;
    abnormalTempo: boolean;
  } {
    const mouseDyn = this.computeMouseDynamics();
    const keyDyn = this.computeKeystrokeDynamics();
    const scrollBeh = this.computeScrollBehavior();
    const sessBeh = this.computeSessionBehavior();

    return {
      perfectMouseLinearity: mouseDyn.sampleCount > 10 && mouseDyn.straightness > 0.95,
      zeroMouseJitter: mouseDyn.sampleCount > 10 && mouseDyn.jitter < 0.05,
      uniformKeystrokeTiming: keyDyn.sampleCount > 10 && keyDyn.rhythmConsistency > 0.9,
      noKeystrokeData: keyDyn.sampleCount === 0 && sessionAgeMs > 30_000,
      programmaticScrolling: scrollBeh.sampleCount > 3 && scrollBeh.smoothness < 0.2,
      noIdlePeriods: sessBeh.idle.count === 0 && sessionAgeMs > 60_000,
      noTouchPressure:
        this.touchSupported && this.touchPressureStats.getCount() > 5 && this.touchPressureStats.getMean() === 0,
      abnormalTempo:
        sessBeh.tempo.interactionsPerMinute > 200 || (sessBeh.tempo.interactionsPerMinute < 1 && sessionAgeMs > 30_000),
    };
  }

  // === DOM Listener attachment ===

  private attachListeners(): void {
    if (this.listenersAttached) return;
    this.listenersAttached = true;

    // --- Mouse (throttled 50ms) ---
    this.boundMouseHandler = throttle((e: MouseEvent) => {
      this.onMouseSample(e.clientX, e.clientY, performance.now());
      this.recordInteraction();
    }, 50);
    window.addEventListener("mousemove", this.boundMouseHandler, { passive: true });

    // --- Keydown ---
    this.boundKeydownHandler = (e: KeyboardEvent) => {
      if (e.repeat) return;
      const now = performance.now();

      // Inter-key delay
      if (this.lastKeydownTs > 0) {
        const gap = now - this.lastKeydownTs;
        if (gap < INTER_KEY_MAX_GAP_MS) {
          this.interKeyStats.push(gap);
        }
      }
      this.lastKeydownTs = now;

      // Flight time (keyup → keydown)
      if (this.lastKeyupTs > 0) {
        const flight = now - this.lastKeyupTs;
        if (flight < INTER_KEY_MAX_GAP_MS) {
          this.flightTimeStats.push(flight);
        }
      }

      this.pendingKeydowns.set(e.code, now);
      this.recordInteraction();
    };
    document.addEventListener("keydown", this.boundKeydownHandler, { passive: true });

    // --- Keyup ---
    this.boundKeyupHandler = (e: KeyboardEvent) => {
      const now = performance.now();
      this.lastKeyupTs = now;

      const downTs = this.pendingKeydowns.get(e.code);
      if (downTs !== undefined) {
        const dwell = now - downTs;
        this.dwellTimeStats.push(dwell);
        this.pendingKeydowns.delete(e.code);
      }
    };
    document.addEventListener("keyup", this.boundKeyupHandler, { passive: true });

    // --- Scroll (throttled 200ms) ---
    this.boundScrollHandler = throttle(() => {
      this.onScrollSample();
      this.recordInteraction();
    }, 200);
    window.addEventListener("scroll", this.boundScrollHandler, { passive: true });

    // --- Visibility change ---
    this.boundVisibilityHandler = () => {
      const now = Date.now();
      if (document.hidden) {
        this.lastHiddenTs = now;
      } else {
        if (this.lastHiddenTs !== null) {
          this.totalHiddenMs += now - this.lastHiddenTs;
          this.lastHiddenTs = null;
        }
        this.tabSwitchCount++;
      }
    };
    document.addEventListener("visibilitychange", this.boundVisibilityHandler);

    // --- Touch (conditional) ---
    if (this.touchSupported) {
      this.boundTouchStartHandler = (e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        this.activeTouchStart = { x: touch.clientX, y: touch.clientY, t: performance.now() };
        this.touchRadiusStats.push((touch.radiusX + touch.radiusY) / 2);
        if (touch.force > 0) this.touchPressureStats.push(touch.force);
        this.recordInteraction();
      };
      document.addEventListener("touchstart", this.boundTouchStartHandler, { passive: true });

      this.boundTouchMoveHandler = throttle((e: TouchEvent) => {
        const touch = e.touches[0];
        if (!touch) return;
        this.touchRadiusStats.push((touch.radiusX + touch.radiusY) / 2);
        if (touch.force > 0) this.touchPressureStats.push(touch.force);
      }, 100);
      document.addEventListener("touchmove", this.boundTouchMoveHandler, { passive: true });

      this.boundTouchEndHandler = (e: TouchEvent) => {
        if (!this.activeTouchStart) return;
        const touch = e.changedTouches[0];
        if (!touch) return;
        const now = performance.now();
        const duration = now - this.activeTouchStart.t;
        const dx = touch.clientX - this.activeTouchStart.x;
        const dy = touch.clientY - this.activeTouchStart.y;
        const displacement = Math.sqrt(dx * dx + dy * dy);

        if (duration < TAP_MAX_DURATION_MS && displacement < SWIPE_MIN_DISPLACEMENT_PX) {
          this.tapCount++;
        } else if (displacement >= SWIPE_MIN_DISPLACEMENT_PX) {
          this.swipeCount++;
          const velocity = displacement / duration;
          this.swipeVelocityStats.push(velocity);
        }
        this.activeTouchStart = null;
      };
      document.addEventListener("touchend", this.boundTouchEndHandler, { passive: true });
    }
  }

  private detachListeners(): void {
    if (this.boundMouseHandler) window.removeEventListener("mousemove", this.boundMouseHandler);
    if (this.boundKeydownHandler) document.removeEventListener("keydown", this.boundKeydownHandler);
    if (this.boundKeyupHandler) document.removeEventListener("keyup", this.boundKeyupHandler);
    if (this.boundScrollHandler) window.removeEventListener("scroll", this.boundScrollHandler);
    if (this.boundVisibilityHandler) document.removeEventListener("visibilitychange", this.boundVisibilityHandler);
    if (this.boundTouchStartHandler) document.removeEventListener("touchstart", this.boundTouchStartHandler);
    if (this.boundTouchMoveHandler) document.removeEventListener("touchmove", this.boundTouchMoveHandler);
    if (this.boundTouchEndHandler) document.removeEventListener("touchend", this.boundTouchEndHandler);
    this.listenersAttached = false;
  }

  // === Mouse sample processing ===

  private onMouseSample(x: number, y: number, t: number): void {
    if (this.lastMouseSample) {
      const dt = t - this.lastMouseSample.t;
      if (dt <= 0) return;

      const dx = x - this.lastMouseSample.x;
      const dy = y - this.lastMouseSample.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const velocity = dist / dt;

      this.mouseVelocityStats.push(velocity);

      // Acceleration
      const dv = Math.abs(velocity - this.lastMouseSample.velocity);
      const accel = dv / dt;
      this.mouseAccelStats.push(accel);

      // Path straightness — track segments between pauses
      const timeSinceLastSample = t - this.lastMouseSampleTs;
      if (timeSinceLastSample > PAUSE_THRESHOLD_MS && this.currentPathLength > 0) {
        // End previous segment
        const dispX = this.lastMouseSample.x - this.currentPathStartX;
        const dispY = this.lastMouseSample.y - this.currentPathStartY;
        const displacement = Math.sqrt(dispX * dispX + dispY * dispY);
        this.pushPathSegment({ displacement, pathLength: this.currentPathLength });
        this.currentPathLength = 0;
        this.currentPathStartX = x;
        this.currentPathStartY = y;
      } else if (this.currentPathLength === 0) {
        this.currentPathStartX = this.lastMouseSample.x;
        this.currentPathStartY = this.lastMouseSample.y;
      }
      this.currentPathLength += dist;

      // Angular jitter
      if (dist > 1) {
        const angle = Math.atan2(dy, dx);
        if (this.lastAngle !== null) {
          let angleDiff = Math.abs(angle - this.lastAngle);
          if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
          this.angularVarianceStats.push(angleDiff);
        }
        this.lastAngle = angle;
      }

      this.lastMouseSample = { x, y, t, velocity };
    } else {
      this.lastMouseSample = { x, y, t, velocity: 0 };
      this.currentPathStartX = x;
      this.currentPathStartY = y;
    }
    this.lastMouseSampleTs = t;
  }

  private pushPathSegment(segment: PathSegment): void {
    if (this.pathSegments.length >= MAX_PATH_SEGMENTS) {
      this.pathSegments.shift();
    }
    this.pathSegments.push(segment);
  }

  // === Scroll sample processing ===

  private onScrollSample(): void {
    const scrollY = window.scrollY;
    const now = performance.now();

    if (scrollY > this.maxScrollDepthPx) {
      this.maxScrollDepthPx = scrollY;
    }

    if (this.lastScrollTs > 0) {
      const dt = now - this.lastScrollTs;
      const deltaY = scrollY - this.lastScrollY;
      const absDelta = Math.abs(deltaY);

      if (dt > 0 && absDelta > 0) {
        const speed = absDelta / dt;
        this.scrollSpeedStats.push(speed);
        this.scrollTotalDistance += absDelta;
        this.totalScrollSamples++;

        // Smoothness: small increments vs large jumps
        if (absDelta < 100) this.smoothScrollCount++;

        // Direction changes
        const dir = deltaY > 0 ? 1 : -1;
        if (this.lastScrollDirection !== 0 && dir !== this.lastScrollDirection) {
          this.scrollDirectionChanges++;
        }
        this.lastScrollDirection = dir;
      }
    }

    this.lastScrollY = scrollY;
    this.lastScrollTs = now;
  }

  // === Interaction tracking ===

  private recordInteraction(): void {
    const now = Date.now();
    this.lastAnyInteractionTs = now;
    this.interactionTimestamps.push(now);

    // Close idle period if active
    if (this.currentIdleStart !== null) {
      const idleDuration = now - this.currentIdleStart;
      this.totalIdleMs += idleDuration;
      if (idleDuration > this.maxIdleMs) this.maxIdleMs = idleDuration;
      this.idleCount++;
      this.currentIdleStart = null;
    }

    // Detect bursts
    const burstCutoff = now - BURST_WINDOW_MS;
    const recentCount = this.interactionTimestamps.filter((t) => t >= burstCutoff).length;
    if (recentCount === BURST_MIN_EVENTS) {
      this.burstCount++;
    }

    // Prune old timestamps (keep last 60s)
    if (this.interactionTimestamps.length > 500) {
      const cutoff = now - INTERACTION_WINDOW_MS;
      this.interactionTimestamps = this.interactionTimestamps.filter((t) => t >= cutoff);
    }
  }

  // === Idle check ===

  private startIdleCheck(): void {
    this.idleCheckIntervalId = setInterval(() => {
      const now = Date.now();
      if (now - this.lastAnyInteractionTs > IDLE_THRESHOLD_MS && this.currentIdleStart === null) {
        this.currentIdleStart = this.lastAnyInteractionTs;
      }
    }, IDLE_CHECK_INTERVAL_MS);
  }

  private stopIdleCheck(): void {
    if (this.idleCheckIntervalId !== null) {
      clearInterval(this.idleCheckIntervalId);
      this.idleCheckIntervalId = null;
    }
  }
}
